/**
 * RemittancesList Component
 *
 * Displays a responsive list of enriched remittances with pagination.
 * Uses CSS Grid layout that adapts from full table to stacked cards on mobile.
 * Mirrors the ClaimsList pattern.
 */

import React, { useCallback, useMemo, useState } from 'react';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { RemittanceListItem } from '@store/generated/billing';

import { useRemittancesList } from '../../../store';

import { RemittanceDetailDialog } from './RemittanceDetailDialog';
import { RemittanceRow } from './RemittanceRow';
import { RemittanceTableHeader } from './RemittanceTableHeader';
import type { TableLayoutMode } from './types';
import { getGridTemplateColumns, GRID_GAP } from './types';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface RemittancesListProps {
  filters: {
    from?: string;
    to?: string;
    payerName?: string;
    paymentMethod?: string;
    queue?: string;
  };
  sort?: string;
  order: 'asc' | 'desc';
  page: number;
  perPage: number;
  onPageChange: (page: number) => void;
  onPerPageChange: (perPage: number) => void;
  onSort: (field: string) => void;
}

// Map UI sort fields to API sort field names
const SORT_FIELD_MAP: Record<string, 'payment_date' | 'total_payment_amount_cents' | 'created_at' | 'payer_name'> = {
  payment_date: 'payment_date',
  total_payment_amount_cents: 'total_payment_amount_cents',
  created_at: 'created_at',
  payer_name: 'payer_name',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RemittancesList: React.FC<RemittancesListProps> = ({
  filters,
  sort,
  order,
  page,
  perPage,
  onPageChange,
}) => {
  const theme = useTheme();

  // Responsive table layout
  const isXl = useMediaQuery(theme.breakpoints.up('xl'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));

  const tableLayout: TableLayoutMode = useMemo(() => {
    if (isXl) return 'full';
    if (isLg) return 'standard';
    if (isMd) return 'compact';
    return 'card';
  }, [isXl, isLg, isMd]);

  // Detail dialog state
  const [selectedRemittanceId, setSelectedRemittanceId] = useState<string | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  // Map sort field
  const apiSortBy = sort && SORT_FIELD_MAP[sort] ? SORT_FIELD_MAP[sort] : undefined;

  const {
    remittances,
    totalCount,
    currentPage,
    totalPages,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useRemittancesList({
    dateFrom: filters.from,
    dateTo: filters.to,
    payerName: filters.payerName,
    paymentMethod: filters.paymentMethod,
    queue: filters.queue,
    page,
    perPage,
    sortBy: apiSortBy,
    sortOrder: apiSortBy ? order : undefined,
  });

  const isEmpty = !isLoading && remittances.length === 0;
  const isListLoading = isLoading || isFetching;
  const skeletonRowCount = Math.min(12, Math.max(6, perPage));

  // Pagination
  const hasPrevious = currentPage > 1;
  const hasMore = currentPage < totalPages;

  const handlePreviousPage = useCallback(() => {
    if (hasPrevious && !isLoading) {
      onPageChange(Math.max(1, currentPage - 1));
    }
  }, [hasPrevious, isLoading, currentPage, onPageChange]);

  const handleNextPage = useCallback(() => {
    if (hasMore && !isLoading) {
      onPageChange(currentPage + 1);
    }
  }, [hasMore, isLoading, currentPage, onPageChange]);

  // Row click → open detail dialog
  const handleRowClick = useCallback((remittance: RemittanceListItem) => {
    setSelectedRemittanceId(remittance.remittance_id);
    setDetailDialogOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailDialogOpen(false);
    setSelectedRemittanceId(null);
  }, []);

  // Skeleton rows
  const renderSkeletonRow = (key: number) => {
    if (tableLayout === 'card') {
      return (
        <Box
          key={key}
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${theme.palette.grey[100]}`,
            '&:last-child': { borderBottom: 'none' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
            <Skeleton variant="text" width="40%" />
            <Skeleton variant="text" width="15%" />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Skeleton variant="text" width="20%" />
            <Skeleton variant="rectangular" width={48} height={18} sx={{ borderRadius: 999 }} />
            <Skeleton variant="text" width="15%" />
          </Box>
        </Box>
      );
    }

    return (
      <Box
        key={key}
        sx={{
          px: 2,
          py: 0.75,
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
          '&:last-child': { borderBottom: 'none' },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: getGridTemplateColumns(tableLayout),
            gap: GRID_GAP,
            alignItems: 'center',
          }}
        >
          <Skeleton variant="text" width="80%" />
          <Skeleton variant="text" width="70%" />
          {(tableLayout === 'full' || tableLayout === 'standard') && <Skeleton variant="text" width="65%" />}
          {tableLayout === 'full' && <Skeleton variant="rectangular" width={48} height={18} sx={{ borderRadius: 999 }} />}
          <Skeleton variant="text" width="85%" sx={{ textAlign: 'right' }} />
          <Skeleton variant="text" width="40%" sx={{ textAlign: 'right' }} />
          {(tableLayout === 'full' || tableLayout === 'standard') && <Skeleton variant="text" width="30%" sx={{ textAlign: 'right' }} />}
        </Box>
      </Box>
    );
  };

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={refetch}>
            Retry
          </Button>
        }
      >
        <AlertTitle>Error loading payments</AlertTitle>
        Failed to load remittance payments. Please try again.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Summary */}
      <Box sx={{ mb: 2 }}>
        {!isListLoading && (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {totalCount} {totalCount === 1 ? 'payment' : 'payments'}
            {isFetching && !isLoading && ' (updating...)'}
          </Typography>
        )}
      </Box>

      {/* Remittances Table */}
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 3,
          overflow: 'hidden',
        }}
      >
        <RemittanceTableHeader layout={tableLayout} />

        {isListLoading ? (
          <Box>
            {Array.from({ length: skeletonRowCount }).map((_, i) => renderSkeletonRow(i))}
          </Box>
        ) : isEmpty ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
              px: 2,
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: theme.palette.text.secondary }}>
              No payments found
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
              Payer remittance payments (835s) will appear here once submitted claims are processed.
            </Typography>
          </Box>
        ) : (
          <Box>
            {remittances.map(remittance => (
              <RemittanceRow
                key={remittance.remittance_id}
                remittance={remittance}
                layout={tableLayout}
                onRowClick={handleRowClick}
              />
            ))}
          </Box>
        )}

        {/* Pagination */}
        {!isEmpty && !isListLoading && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderTop: `1px solid ${theme.palette.grey[200]}`,
              bgcolor: theme.palette.grey[50],
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Page {currentPage} of {totalPages} ({totalCount} total)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton onClick={handlePreviousPage} disabled={!hasPrevious || isFetching} size="small">
                <ChevronLeftIcon />
              </IconButton>
              <IconButton onClick={handleNextPage} disabled={!hasMore || isFetching} size="small">
                <ChevronRightIcon />
              </IconButton>
            </Box>
          </Box>
        )}
      </Paper>

      {/* Detail Dialog */}
      <RemittanceDetailDialog
        open={detailDialogOpen}
        onClose={handleCloseDetail}
        remittanceId={selectedRemittanceId}
      />
    </Box>
  );
};
